import { useState, useRef, useCallback, useEffect } from "react";
import { DialogStateHookProps } from "./DialogStateInternalTypes";

export function useDialogStateCore({
  open,
  onOpenChange,
  isSubmitting,
  forceKeepOpen: externalForceKeepOpen,
  installationPhase = false,
}: DialogStateHookProps) {
  // Internal state to track dialog open state
  const [internalOpen, setInternalOpen] = useState(open);
  
  // State to control force keeping dialog open
  const [forceKeepOpen, setForceKeepOpen] = useState(externalForceKeepOpen);
  
  // State tracking if the dialog has been explicitly closed by the user
  const [isExplicitlyClosed, setExplicitlyClosed] = useState(false);
  
  // State tracking if content is mounted and ready
  const [contentMounted, setContentMounted] = useState(false);
  
  // Various refs for tracking lifecycle events
  const contentMountedRef = useRef(false);
  const prevOpenRef = useRef(open);
  const openStateChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastStateChangeTimestampRef = useRef<number>(0);
  const isInitialMountRef = useRef(true);
  const installedRef = useRef(false);
  
  // Compute whether dialog close should be blocked
  const shouldBlockClose = isSubmitting || forceKeepOpen;
  
  // Reset explicit closed state
  const resetExplicitlyClosed = useCallback(() => {
    setExplicitlyClosed(false);
  }, []);
  
  // Update internal state when props change
  useEffect(() => {
    if (prevOpenRef.current !== open) {
      prevOpenRef.current = open;
      setInternalOpen(open);
    }
    
    if (externalForceKeepOpen !== forceKeepOpen) {
      setForceKeepOpen(externalForceKeepOpen);
    }
  }, [open, externalForceKeepOpen, forceKeepOpen]);
  
  // Handle dialog open state changes
  const handleOpenChange = useCallback((open: boolean) => {
    // Update timestamp to track last state change
    lastStateChangeTimestampRef.current = Date.now();
    
    // Prevent closing in critical state
    if (!open && shouldBlockClose) {
      return;
    }
    
    // Track explicit closure
    if (!open) {
      setExplicitlyClosed(true);
    } else {
      // Reset when reopening
      setExplicitlyClosed(false);
    }
    
    setInternalOpen(open);
    onOpenChange(open);
  }, [onOpenChange, shouldBlockClose]);
  
  // Update content mounted state based on ref
  useEffect(() => {
    setContentMounted(contentMountedRef.current);
  }, [contentMountedRef.current]);
  
  // Mark initial mount as complete after first render
  useEffect(() => {
    isInitialMountRef.current = false;
  }, []);

  return {
    internalOpen,
    setInternalOpen,
    forceKeepOpen,
    setForceKeepOpen,
    isExplicitlyClosed,
    setExplicitlyClosed,
    contentMounted,
    shouldBlockClose,
    handleOpenChange,
    resetExplicitlyClosed,
    contentMountedRef,
    prevOpenRef,
    openStateChangeTimeoutRef,
    lastStateChangeTimestampRef,
    isInitialMountRef,
    installedRef
  };
}
